import React, { useState, useMemo } from 'react';
import { Tv, Plus, Trash2, Edit2, Check, X, DollarSign, Gift, CreditCard, Package, Calendar, Wifi } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';

const SubscriptionsSection = () => {
  const {
    subscriptions,
    bundles,
    creditCards,
    memberships,
    activePerks,
    refData,
    expandedSections,
    saving,
    addSubscription,
    removeSubscription,
    updateSubscription,
    saveSubscription,
    deleteSubscription,
    toggleSection,
    isSectionDirty,
    profile,
  } = useProfile();

  const isExpanded = expandedSections.includes('subscriptions');
  const isDirty = isSectionDirty('subscriptions');

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newService, setNewService] = useState('');

  // Helper to get retail price for a service from reference data
  // Only for display comparison - actual costs should come from user input
  const getRetailPrice = (serviceName) => {
    const serviceRef = refData?.streamingServices?.find(
      s => s.service_name?.toLowerCase() === serviceName?.toLowerCase()
    );
    // Only use reference data, no fallbacks
    return serviceRef?.base_price_monthly || 
           serviceRef?.base_price || 
           serviceRef?.monthly_price ||
           serviceRef?.retail_price ||
           serviceRef?.price ||
           0;
  };

  // Get streaming services from reference data
  const serviceOptions = useMemo(() => {
    if (refData.streamingServices?.length > 0) {
      return refData.streamingServices.map(s => s.service_name);
    }
    // Fallback
    return [
      'Netflix', 'Disney+', 'Hulu', 'Amazon Prime Video', 'Apple TV+',
      'Max (HBO Max)', 'Paramount+', 'Peacock', 'Discovery+', 'ESPN+',
      'YouTube TV', 'Sling TV', 'FuboTV', 'Showtime', 'Starz',
      'AMC+', 'BET+', 'Crunchyroll', 'Shudder',
    ];
  }, [refData.streamingServices]);

  // Build a set of services that are included in user's bundles
  const bundledServiceNames = useMemo(() => {
    const names = new Set();
    (bundles || []).forEach(userBundle => {
      // Check joined bundle data first
      const bundleServices = userBundle.bundles?.bundle_services || [];
      bundleServices.forEach(bs => {
        names.add(bs.service_name?.toLowerCase());
      });
      
      // Also check refData bundles
      const bundleRef = refData?.bundles?.find(b => b.bundle_code === userBundle.bundle_code);
      if (bundleRef?.bundle_services) {
        bundleRef.bundle_services.forEach(bs => {
          names.add(bs.service_name?.toLowerCase());
        });
      }
    });
    return names;
  }, [bundles, refData?.bundles]);

  // Categorize subscriptions
  const categorizedSubscriptions = useMemo(() => {
    const bundled = [];
    const ispPerks = [];
    const membershipPerks = [];
    const cardCredit = [];
    const individual = [];
    const needsPricing = []; // Services that need user to enter cost

    subscriptions.forEach(sub => {
      const serviceLower = sub.service_name?.toLowerCase();
      const creditApplied = parseFloat(sub.credit_applied) || 0;
      // Database cost takes priority - this is what the user actually pays
      const dbCost = parseFloat(sub.monthly_cost) || parseFloat(sub.actual_cost) || 0;
      const retailPrice = getRetailPrice(sub.service_name); // Only for display/comparison
      
      // Check if this service is in a bundle (by payment_method OR by being in bundledServiceNames)
      if (sub.payment_method === 'bundle' || bundledServiceNames.has(serviceLower)) {
        // Find which bundle contains this service
        let bundleName = sub.payment_source;
        let bundleCode = sub.payment_source;
        
        // Look up bundle name
        if (bundleCode) {
          const bundleRef = refData?.bundles?.find(b => b.bundle_code === bundleCode);
          bundleName = bundleRef?.bundle_name || bundleCode;
        } else {
          // Find from user's bundles
          const containingBundle = (bundles || []).find(ub => {
            const services = ub.bundles?.bundle_services || 
                            refData?.bundles?.find(b => b.bundle_code === ub.bundle_code)?.bundle_services || [];
            return services.some(bs => bs.service_name?.toLowerCase() === serviceLower);
          });
          if (containingBundle) {
            bundleCode = containingBundle.bundle_code;
            bundleName = containingBundle.bundles?.bundle_name || 
                        refData?.bundles?.find(b => b.bundle_code === bundleCode)?.bundle_name ||
                        bundleCode;
          }
        }
        
        bundled.push({
          ...sub,
          bundleName,
          bundleCode,
          retailPrice,
        });
      } else if (sub.payment_method === 'isp_perk' || sub.payment_method === 'isp_bundle') {
        ispPerks.push({
          ...sub,
          retailPrice,
        });
      } else if (sub.payment_method === 'membership_perk' || sub.payment_method === 'membership_benefit' || sub.payment_method === 'nested_perk') {
        membershipPerks.push({
          ...sub,
          retailPrice,
        });
      } else if (creditApplied > 0 || sub.payment_method === 'credit_card_perk' || sub.payment_method === 'card_perk' || sub.payment_method === 'card_credit') {
        // Has credit applied or marked as card perk
        // If no cost in DB, flag for user input
        if (dbCost === 0 && creditApplied > 0) {
          // User allocated credit but didn't enter cost - needs attention
          cardCredit.push({
            ...sub,
            retailPrice,
            effectiveCost: dbCost,
            creditApplied,
            remainingCost: 0,
            needsCost: true, // Flag that user needs to enter their actual cost
          });
        } else {
          cardCredit.push({
            ...sub,
            retailPrice,
            effectiveCost: dbCost,
            creditApplied,
            remainingCost: Math.max(0, dbCost - creditApplied),
            needsCost: false,
          });
        }
      } else {
        // Check if it's an ISP perk by matching against active perks
        const isIspPerk = activePerks?.some(p => 
          p.service_name?.toLowerCase() === serviceLower && 
          p.is_active &&
          (p.perk_source?.toLowerCase().includes('xfinity') || 
           p.perk_source?.toLowerCase().includes('comcast') ||
           p.perk_source === profile?.internetProvider)
        );
        
        if (isIspPerk) {
          ispPerks.push({
            ...sub,
            retailPrice,
          });
        } else {
          individual.push({
            ...sub,
            retailPrice,
            effectiveCost: dbCost,
            needsCost: dbCost === 0, // Flag if user needs to enter cost
          });
        }
      }
    });

    return { bundled, ispPerks, membershipPerks, cardCredit, individual, needsPricing };
  }, [subscriptions, bundles, bundledServiceNames, refData?.bundles, activePerks, profile?.internetProvider]);

  // Filter out already added services
  const availableServices = useMemo(() => {
    const existingServices = subscriptions.map(s => s.service_name?.toLowerCase());
    // Also exclude services in bundles
    return serviceOptions.filter(s => 
      !existingServices.includes(s.toLowerCase()) &&
      !bundledServiceNames.has(s.toLowerCase())
    );
  }, [serviceOptions, subscriptions, bundledServiceNames]);

  const handleAddSubscription = async () => {
    if (!newService) return;

    const retailPrice = getRetailPrice(newService);
    const sub = addSubscription({
      service_name: newService,
      subscription_tier: '',
      monthly_cost: retailPrice || 0,
      retail_cost: retailPrice || 0,
      billing_cycle: 'monthly',
      payment_method: 'paid',
      payment_source: '',
      credit_applied: 0,
      actual_cost: retailPrice || 0,
    });

    await saveSubscription(sub);

    setNewService('');
    setShowAddForm(false);
    setEditingId(sub.id);
  };

  const handleRemoveSubscription = async (subscriptionId) => {
    const sub = subscriptions.find(s => s.id === subscriptionId);
    if (window.confirm(`Remove ${sub?.service_name}? This will also delete any credit allocations.`)) {
      await deleteSubscription(subscriptionId);
    }
  };

  // Calculate totals for individual subscriptions only (using database costs)
  const totalMonthlyCost = useMemo(() => {
    // Individual paid services (cost from database)
    const individualCost = categorizedSubscriptions.individual.reduce((total, sub) => {
      const cost = sub.effectiveCost || 0; // Only use DB cost, not fallback
      return total + cost;
    }, 0);
    
    // Card credit services (remaining after credit, using DB cost)
    const cardCreditCost = categorizedSubscriptions.cardCredit.reduce((total, sub) => {
      return total + (sub.remainingCost || 0);
    }, 0);
    
    return individualCost + cardCreditCost;
  }, [categorizedSubscriptions]);
  
  // Count services that need pricing
  const servicesNeedingPricing = useMemo(() => {
    return [
      ...categorizedSubscriptions.individual.filter(s => s.needsCost),
      ...categorizedSubscriptions.cardCredit.filter(s => s.needsCost),
    ];
  }, [categorizedSubscriptions]);

  // Summary counts non-bundled subscriptions
  const individualCount = categorizedSubscriptions.individual.length + categorizedSubscriptions.cardCredit.length;
  const summaryText = useMemo(() => {
    if (individualCount === 0) return 'No paid subscriptions';
    const needsPricingCount = servicesNeedingPricing.length;
    if (needsPricingCount > 0) {
      return `${individualCount} service${individualCount > 1 ? 's' : ''} • ⚠️ ${needsPricingCount} need${needsPricingCount === 1 ? 's' : ''} pricing`;
    }
    return `${individualCount} paid service${individualCount > 1 ? 's' : ''} • $${totalMonthlyCost.toFixed(2)}/mo`;
  }, [individualCount, totalMonthlyCost, servicesNeedingPricing]);

  // Total badge shows all subscriptions
  const totalCount = subscriptions.length;

  return (
    <SectionWrapper
      title="Subscriptions"
      icon={Tv}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('subscriptions')}
      isDirty={isDirty}
      isSaving={saving}
      summary={summaryText}
      badge={totalCount > 0 ? totalCount : null}
    >
      <div className="space-y-4">
        
        {/* ===== BUNDLED SERVICES ===== */}
        {categorizedSubscriptions.bundled.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-purple-700 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Included in Bundles
            </h4>
            {categorizedSubscriptions.bundled.map(sub => (
              <div 
                key={sub.id} 
                className="border border-purple-200 bg-purple-50/50 rounded-lg p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{sub.service_name}</span>
                    {sub.subscription_tier && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                        {sub.subscription_tier}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {sub.retailPrice > 0 && (
                      <span className="text-sm text-gray-400 line-through">
                        ${sub.retailPrice.toFixed(2)}/mo
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-1 text-sm text-purple-600">
                  <Package className="w-3 h-3" />
                  <span>Included in {sub.bundleName || 'Bundle'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== ISP PERKS ===== */}
        {categorizedSubscriptions.ispPerks.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-blue-700 flex items-center gap-2">
              <Wifi className="w-4 h-4" />
              Free with Internet
            </h4>
            {categorizedSubscriptions.ispPerks.map(sub => (
              <div 
                key={sub.id} 
                className="border border-blue-200 bg-blue-50/50 rounded-lg p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{sub.service_name}</span>
                    {sub.subscription_tier && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                        {sub.subscription_tier}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {sub.retailPrice > 0 && (
                      <span className="text-sm text-gray-400 line-through">
                        ${sub.retailPrice.toFixed(2)}/mo
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-1 text-sm text-blue-600">
                  <Wifi className="w-3 h-3" />
                  <span>Included with {profile?.internetProvider || 'ISP'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== MEMBERSHIP PERKS ===== */}
        {categorizedSubscriptions.membershipPerks.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-green-700 flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Free with Memberships
            </h4>
            {categorizedSubscriptions.membershipPerks.map(sub => (
              <div 
                key={sub.id} 
                className="border border-green-200 bg-green-50/50 rounded-lg p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{sub.service_name}</span>
                    {sub.subscription_tier && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                        {sub.subscription_tier}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {sub.retailPrice > 0 && (
                      <span className="text-sm text-gray-400 line-through">
                        ${sub.retailPrice.toFixed(2)}/mo
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-1 text-sm text-green-600">
                  <Gift className="w-3 h-3" />
                  <span>Via {sub.payment_source || 'Membership'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== CARD CREDIT SERVICES ===== */}
        {categorizedSubscriptions.cardCredit.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-emerald-700 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Card Credits Applied
            </h4>
            {categorizedSubscriptions.cardCredit.map(sub => {
              const isEditing = editingId === sub.id;
              const cost = sub.effectiveCost || 0;
              const credit = sub.creditApplied || 0;
              const remaining = sub.remainingCost || 0;
              const isFullyCovered = credit >= cost && cost > 0;
              const needsCost = sub.needsCost;

              if (isEditing) {
                return (
                  <SubscriptionEditForm
                    key={sub.id}
                    subscription={sub}
                    creditCards={creditCards}
                    memberships={memberships}
                    refData={refData}
                    onSave={async (updatedSub) => {
                      updateSubscription(updatedSub);
                      await saveSubscription(updatedSub);
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }

              return (
                <div 
                  key={sub.id} 
                  className={`border rounded-lg p-3 ${
                    needsCost 
                      ? 'border-amber-300 bg-amber-50/50' 
                      : isFullyCovered 
                        ? 'border-green-200 bg-green-50/50' 
                        : 'border-emerald-200 bg-emerald-50/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{sub.service_name}</span>
                        {sub.subscription_tier && (
                          <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                            {sub.subscription_tier}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-sm flex-wrap">
                        {needsCost ? (
                          // Cost not entered - prompt user
                          <span className="text-amber-600 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>${credit.toFixed(2)} credit allocated</span>
                            <span className="text-amber-700 font-medium">— enter your monthly cost</span>
                          </span>
                        ) : cost > 0 ? (
                          <>
                            <span className="text-gray-600">${cost.toFixed(2)}/mo</span>
                            {credit > 0 && (
                              <>
                                <span className="text-emerald-600">-${credit.toFixed(2)} credit</span>
                                {isFullyCovered ? (
                                  <span className="font-medium text-green-600 flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    FREE
                                  </span>
                                ) : (
                                  <span className="font-medium text-gray-700">= ${remaining.toFixed(2)}/mo</span>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <span className="text-amber-600">Click edit to enter your cost</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingId(sub.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          needsCost 
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-100 animate-pulse' 
                            : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveSubscription(sub.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===== INDIVIDUAL PAID SERVICES ===== */}
        {categorizedSubscriptions.individual.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Paid Services
            </h4>
            {categorizedSubscriptions.individual.map(sub => {
              const isEditing = editingId === sub.id;
              const cost = sub.effectiveCost || 0;
              const needsCost = sub.needsCost;

              if (isEditing) {
                return (
                  <SubscriptionEditForm
                    key={sub.id}
                    subscription={sub}
                    creditCards={creditCards}
                    memberships={memberships}
                    refData={refData}
                    onSave={async (updatedSub) => {
                      updateSubscription(updatedSub);
                      await saveSubscription(updatedSub);
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }

              return (
                <div 
                  key={sub.id} 
                  className={`border rounded-lg p-3 ${
                    needsCost ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{sub.service_name}</span>
                        {sub.subscription_tier && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                            {sub.subscription_tier}
                          </span>
                        )}
                        {sub.billing_cycle === 'annual' && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Annual
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-sm">
                        {needsCost ? (
                          <span className="text-amber-600 flex items-center gap-1">
                            <span>⚠️</span>
                            <span className="font-medium">Enter your monthly cost</span>
                          </span>
                        ) : cost > 0 ? (
                          <>
                            <DollarSign className="w-3 h-3 text-gray-600" />
                            <span className="text-gray-600">
                              ${cost.toFixed(2)}{sub.billing_cycle === 'annual' ? '/yr' : '/mo'}
                              {sub.billing_cycle === 'annual' && (
                                <span className="text-gray-400 ml-1">
                                  (${(cost / 12).toFixed(2)}/mo)
                                </span>
                              )}
                            </span>
                          </>
                        ) : (
                          <span className="text-amber-600">Click edit to enter your cost</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingId(sub.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          needsCost 
                            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-100 animate-pulse' 
                            : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveSubscription(sub.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {subscriptions.length === 0 && !showAddForm && (
          <div className="text-center py-6 text-gray-500">
            <Tv className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No subscriptions tracked yet</p>
            <p className="text-xs mt-1">Add your streaming services to start optimizing</p>
          </div>
        )}

        {/* Add Subscription Form */}
        {showAddForm && (
          <div className="border-2 border-purple-200 rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-gray-900">Add Subscription</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Service
              </label>
              <select
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose a service...</option>
                {availableServices.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>

            {newService === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Name
                </label>
                <input
                  type="text"
                  value={newService === 'Other' ? '' : newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="Enter service name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewService('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubscription}
                disabled={!newService || newService === 'Other'}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
              >
                Add & Configure
              </button>
            </div>
          </div>
        )}

        {/* Add Button */}
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 hover:border-purple-400 text-gray-600 hover:text-purple-600 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Subscription
          </button>
        )}

        {/* Total */}
        {individualCount > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="font-medium text-gray-700">Monthly Cost (Paid Services)</span>
            <span className="text-xl font-bold text-gray-900">${totalMonthlyCost.toFixed(2)}</span>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

// Subscription Edit Form Component (unchanged from original)
const SubscriptionEditForm = ({ subscription, creditCards, memberships, refData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    ...subscription,
    monthly_cost: subscription.monthly_cost || 0,
    credit_applied: subscription.credit_applied || 0,
    actual_cost: subscription.actual_cost || subscription.monthly_cost || 0,
    billing_cycle: subscription.billing_cycle || 'monthly',
  });

  // Get tier options for this service from reference data
  const tierOptions = useMemo(() => {
    const service = refData.streamingServices?.find(
      s => s.service_name?.toLowerCase() === subscription.service_name?.toLowerCase()
    );
    
    if (!service) return [];
    
    const tiers = [];
    const basePrice = service.base_price_monthly || service.base_price || service.monthly_price || 0;
    const premiumPrice = service.premium_price_monthly || service.premium_price || 0;
    const adPrice = service.ad_supported_price || service.with_ads_price || 0;
    
    if (adPrice > 0) {
      tiers.push({ 
        name: 'With Ads', 
        monthly: adPrice,
        annual: adPrice * 12
      });
    }
    if (basePrice > 0) {
      tiers.push({ 
        name: 'Standard', 
        monthly: basePrice,
        annual: service.base_price_annual || basePrice * 12
      });
    }
    if (premiumPrice > 0) {
      tiers.push({ 
        name: 'Premium', 
        monthly: premiumPrice,
        annual: service.premium_price_annual || premiumPrice * 12
      });
    }
    
    return tiers;
  }, [refData.streamingServices, subscription.service_name]);

  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      payment_method: method,
      payment_source: '',
      credit_applied: 0,
      actual_cost: method === 'paid' ? prev.monthly_cost : 0,
    }));
  };

  const handleTierSelect = (tier) => {
    const cost = formData.billing_cycle === 'annual' ? tier.annual : tier.monthly;
    setFormData(prev => ({
      ...prev,
      subscription_tier: tier.name,
      monthly_cost: tier.monthly,
      actual_cost: prev.payment_method === 'paid' ? cost : 0,
    }));
  };

  const handleBillingCycleChange = (cycle) => {
    const currentCost = formData.actual_cost || formData.monthly_cost || 0;
    let newActualCost;
    
    if (cycle === 'annual' && formData.billing_cycle === 'monthly') {
      newActualCost = currentCost * 12;
    } else if (cycle === 'monthly' && formData.billing_cycle === 'annual') {
      newActualCost = currentCost / 12;
    } else {
      newActualCost = currentCost;
    }
    
    setFormData(prev => ({
      ...prev,
      billing_cycle: cycle,
      actual_cost: prev.payment_method === 'paid' ? newActualCost : 0,
      monthly_cost: cycle === 'annual' ? newActualCost / 12 : newActualCost,
    }));
  };

  const handleSave = () => {
    let actualCost = formData.actual_cost || formData.monthly_cost;
    
    if (formData.payment_method === 'credit_card_perk' || formData.payment_method === 'card_perk') {
      actualCost = Math.max(0, formData.monthly_cost - (formData.credit_applied || 0));
    } else if (['membership_benefit', 'isp_bundle', 'nested_perk', 'bundle'].includes(formData.payment_method)) {
      actualCost = 0;
    }
    
    const monthlyCost = formData.billing_cycle === 'annual' 
      ? (formData.actual_cost / 12) 
      : formData.monthly_cost;
    
    onSave({
      ...formData,
      monthly_cost: monthlyCost,
      actual_cost: actualCost,
    });
  };

  return (
    <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50">
      <h4 className="font-semibold text-gray-900 mb-4">{subscription.service_name}</h4>

      <div className="space-y-4">
        {/* Tier Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subscription Tier
          </label>
          {tierOptions.length > 0 ? (
            <div className="space-y-2">
              {tierOptions.map(tier => (
                <label
                  key={tier.name}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border ${
                    formData.subscription_tier === tier.name
                      ? 'border-purple-500 bg-purple-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="tier"
                      checked={formData.subscription_tier === tier.name}
                      onChange={() => handleTierSelect(tier)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="font-medium text-gray-900">{tier.name}</span>
                  </div>
                  <span className="text-gray-600">${tier.monthly.toFixed(2)}/mo</span>
                </label>
              ))}
              <label
                className={`flex items-center p-3 rounded-lg cursor-pointer border ${
                  !tierOptions.find(t => t.name === formData.subscription_tier)
                    ? 'border-purple-500 bg-purple-100'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="tier"
                  checked={!tierOptions.find(t => t.name === formData.subscription_tier)}
                  onChange={() => setFormData(prev => ({ ...prev, subscription_tier: 'Custom' }))}
                  className="w-4 h-4 text-purple-600 mr-3"
                />
                <span className="font-medium text-gray-900">Other tier</span>
              </label>
            </div>
          ) : (
            <input
              type="text"
              value={formData.subscription_tier || ''}
              onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
              placeholder="e.g., Premium, Standard, Basic"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          )}
        </div>

        {/* Billing Cycle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleBillingCycleChange('monthly')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                formData.billing_cycle === 'monthly'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => handleBillingCycleChange('annual')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                formData.billing_cycle === 'annual'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            How do you get this service?
          </label>
          <select
            value={formData.payment_method || 'paid'}
            onChange={(e) => handlePaymentMethodChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="paid">Paid Out of Pocket</option>
            <option value="credit_card_perk">Credit Card Benefit</option>
            <option value="membership_benefit">Membership Benefit</option>
            <option value="isp_bundle">Included with Internet</option>
          </select>
        </div>

        {/* Cost for Paid */}
        {formData.payment_method === 'paid' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Cost ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.actual_cost || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                setFormData({ 
                  ...formData, 
                  actual_cost: value,
                  monthly_cost: formData.billing_cycle === 'annual' ? value / 12 : value,
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={formData.billing_cycle === 'annual' ? "99.99" : "15.99"}
            />
            {formData.billing_cycle === 'annual' && formData.actual_cost > 0 && (
              <p className="text-xs text-gray-500 mt-1">≈ ${(formData.actual_cost / 12).toFixed(2)}/month</p>
            )}
          </div>
        )}

        {/* Credit Card Perk */}
        {(formData.payment_method === 'credit_card_perk' || formData.payment_method === 'card_perk') && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Cost (before credits)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.monthly_cost || ''}
                onChange={(e) => setFormData({ ...formData, monthly_cost: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="15.99"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Which credit card?</label>
              <select
                value={formData.payment_source || ''}
                onChange={(e) => setFormData({ ...formData, payment_source: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select card...</option>
                {creditCards.map(card => (
                  <option key={card.id} value={card.id}>
                    {card.card_name}
                    {card.streaming_credit_monthly > 0 && ` ($${card.streaming_credit_monthly}/mo credit)`}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Set credit allocation in the Credit Cards section</p>
            </div>
          </>
        )}

        {/* Membership Benefit */}
        {formData.payment_method === 'membership_benefit' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Which membership?</label>
            <select
              value={formData.payment_source || ''}
              onChange={(e) => setFormData({ ...formData, payment_source: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select membership...</option>
              {memberships.map(m => (
                <option key={m.id} value={m.membership_type}>{m.membership_name}</option>
              ))}
            </select>
          </div>
        )}

        {/* ISP Bundle */}
        {formData.payment_method === 'isp_bundle' && (
          <p className="text-sm text-green-600">✓ This service is included free with your internet provider</p>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg"
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsSection;